import { Ref, forwardRef } from 'react'
import styled from 'styled-components'
import useIsReadOnly from '../../../hooks/useIsReadOnly'
import { useCompleteRoutineItem } from '../../../services/api/routines.hooks'
import { Border, Colors, Spacing, Typography } from '../../../styles'
import { pointerQuery } from '../../../styles/dimensions'
import { icons } from '../../../styles/images'
import { tokens } from '../../../styles/tokens'
import { TRoutineItem } from '../../../utils/types'
import GTCheckbox from '../../atoms/GTCheckbox'
import { Icon } from '../../atoms/Icon'
import { BodyMedium, BodySmall } from '../../atoms/typography/Typography'
import { PaginateTextButton, ViewHeader, ViewName } from '../styles'
import { ViewItemsProps } from './viewItems.types'

const Row = styled.div<{ $complete: boolean }>`
    display: flex;
    align-items: center;
    gap: ${Spacing._12};
    min-height: ${Spacing._40};
    padding: 0 ${Spacing._8};
    border-radius: ${Border.radius.medium};
    ${Typography.body.medium};
    color: ${tokens.text.secondary};
    ${({ $complete }) =>
        $complete &&
        `
        color: ${Colors.text.muted};
        text-decoration: line-through;
    `}
`

const StreakFooter = styled.div`
    display: flex;
    align-items: center;
    padding: ${Spacing._8} ${Spacing._8} 0;
    margin-top: ${Spacing._8};
    border-top: 1px solid ${Colors.background.hairline};
`

const Empty = styled(BodyMedium)`
    color: ${Colors.text.muted};
    padding: ${Spacing._8};
`

const Streak = styled(BodySmall)`
    display: flex;
    align-items: center;
    gap: ${Spacing._4};
    color: ${Colors.text.muted};
`

const CompletedItemsButton = styled(PaginateTextButton)`
    display: flex;
    align-items: center;
    min-height: ${tokens.hit.pointer};

    ${pointerQuery.noHover} {
        min-height: ${tokens.hit.touch};
    }
`

const RoutineViewItems = forwardRef(
    (
        { view, hideHeader, showCompletedRoutineItems, onToggleCompletedRoutineItems }: ViewItemsProps,
        ref: Ref<HTMLDivElement>
    ) => {
        const { mutate: completeItem, isLoading: isCompletionPending } = useCompleteRoutineItem()
        const isReadOnly = useIsReadOnly()
        const items = view.view_items as TRoutineItem[]
        const completedItemsCount = items.filter((item) => item.is_completed).length
        const visibleItems = showCompletedRoutineItems ? items : items.filter((item) => !item.is_completed)
        const isBlocked = view.status === 'up_next'

        return (
            <>
                {!hideHeader && (
                    <ViewHeader ref={ref}>
                        <ViewName>{view.name}</ViewName>
                    </ViewHeader>
                )}
                {completedItemsCount > 0 && (
                    <CompletedItemsButton
                        type="button"
                        aria-expanded={showCompletedRoutineItems}
                        onClick={onToggleCompletedRoutineItems}
                    >
                        {showCompletedRoutineItems ? 'Hide completed' : `Show completed (${completedItemsCount})`}
                    </CompletedItemsButton>
                )}
                {items.length === 0 ? (
                    <Empty>No steps yet</Empty>
                ) : (
                    visibleItems.map((item) => (
                        <Row key={item.id} $complete={item.is_completed} data-testid={`routine-item-${item.id}`}>
                            <GTCheckbox
                                isChecked={item.is_completed}
                                // A pending step has no server id yet, so it cannot be checked until creation settles.
                                disabled={isReadOnly || isBlocked || isCompletionPending || Boolean(item.optimisticId)}
                                animated
                                onChange={(is_completed) => completeItem({ id: item.id, is_completed })}
                            />
                            <span>{item.title}</span>
                        </Row>
                    ))
                )}
                {Boolean(view.streak) && (
                    <StreakFooter>
                        <Streak>
                            <Icon icon={icons.fire} size="small" color="gray" />
                            {view.streak} day streak
                        </Streak>
                    </StreakFooter>
                )}
            </>
        )
    }
)

export default RoutineViewItems
